import { Controller } from "@hotwired/stimulus";
import Dropzone from "dropzone";

Dropzone.autoDiscover = false;

export default class extends Controller {
  static targets = ["dropzone", "preview", "fileSelect", "submitArea", "submitButton", "loading"];
  static values = {
    uploadUrl: String,
    acceptedFiles: String,
    maxFiles: Number,
    maxFilesize: Number,
  };

  connect() {
    this.uploadedFiles = [];
    this.initializeDropzone();
  }

  disconnect() {
    if (this.dropzone) {
      this.dropzone.destroy();
    }
  }

  initializeDropzone() {
    this.dropzone = new Dropzone(this.dropzoneTarget, {
      url: this.uploadUrlValue,
      acceptedFiles: this.acceptedFilesValue,
      maxFiles: this.maxFilesValue,
      maxFilesize: this.maxFilesizeValue,
      autoProcessQueue: true,
      paramName: "attachment[file]",
      clickable: this.fileSelectTarget,
      previewsContainer: this.previewTarget,
      headers: {
        "X-CSRF-Token": this.getCsrfToken(),
      },
      previewTemplate: this.previewTemplate(),
    });

    this.dropzone.on("addedfile", (file) => this.handleFileAdded(file));
    this.dropzone.on("success", (file, response) => this.handleUploadSuccess(file, response));
    this.dropzone.on("error", (file, error) => this.handleUploadError(file, error));
    this.dropzone.on("removedfile", (file) => this.handleFileRemoved(file));
  }

  handleFileAdded(file) {
    const icon = file.previewElement?.querySelector('[data-dz-file-icon]')
    if (icon) icon.className = `fas ${this.fileIconClass(file.name)} fa-3x me-3`
  }

  fileIconClass(filename) {
    const extension = (filename.split('.').pop() || '').toLowerCase()
    switch (extension) {
      case 'xlsx':
      case 'xls':
        return 'fa-file-excel text-success'
      case 'csv':
        return 'fa-file-csv text-info'
      case 'docx':
      case 'doc':
        return 'fa-file-word text-primary'
      case 'pdf':
        return 'fa-file-pdf text-danger'
      default:
        return 'fa-file text-muted'
    }
  }

  handleUploadSuccess(file, response) {
    if (response.success && response.id) {
      file.attachmentId = response.id;
      this.uploadedFiles.push({ id: response.id, name: file.name });
      this.updateSubmitButton();
    } else {
      this.showError("Upload succeeded but no attachment ID received");
      this.dropzone.removeFile(file);
    }
  }

  handleUploadError(file, error) {
    console.error("Upload error:", error);
    let errorMessage = "Error uploading file. Please try again.";
    
    if (typeof error === 'object' && error.errors) {
      errorMessage = error.errors.join(", ");
    } else if (typeof error === 'string') {
      errorMessage = error;
    }
    
    this.showError(errorMessage);
    this.dropzone.removeFile(file);
  }

  handleFileRemoved(file) {
    if (file.attachmentId) {
      this.uploadedFiles = this.uploadedFiles.filter((uploaded) => uploaded.id !== file.attachmentId);
    }
    this.updateSubmitButton();
  }

  hasPdf() {
    return this.uploadedFiles.some((uploaded) => /\.pdf$/i.test(uploaded.name));
  }

  updateSubmitButton() {
    if (this.uploadedFiles.length === 0) {
      this.submitAreaTarget.classList.add("d-none");
      return;
    }

    this.submitAreaTarget.classList.remove("d-none");
    this.submitButtonTarget.disabled = !this.hasPdf();
    this.submitButtonTarget.innerHTML = `
      <i class="fas fa-magic me-2"></i>
      Generate Quote with AI
    `;
  }

  async generateQuote() {
    if (this.uploadedFiles.length === 0 || !this.hasPdf()) {
      this.showError("Please upload at least one tax return PDF.");
      return;
    }

    // Disable the button and show loading
    this.submitButtonTarget.disabled = true;
    this.submitAreaTarget.classList.add("d-none");
    this.loadingTarget.classList.remove("d-none");

    try {
      const response = await fetch("/admin/ai/tax_quotes/bulk_create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.getCsrfToken(),
        },
        body: JSON.stringify({ attachment_ids: this.uploadedFiles.map((uploaded) => uploaded.id) }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Redirect to the index with count
        window.location.href = data.redirect_url;
      } else {
        this.showError(data.error || "Error generating quotes");
        this.loadingTarget.classList.add("d-none");
        this.submitAreaTarget.classList.remove("d-none");
        this.submitButtonTarget.disabled = false;
      }
    } catch (error) {
      console.error("Error:", error);
      this.showError("Error generating quotes. Please try again.");
      this.loadingTarget.classList.add("d-none");
      this.submitAreaTarget.classList.remove("d-none");
      this.submitButtonTarget.disabled = false;
    }
  }

  showError(message) {
    // Create a simple alert for errors
    const alertDiv = document.createElement('div');
    alertDiv.className = 'alert alert-danger alert-dismissible fade show mt-3';
    alertDiv.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `;
    
    // Insert before the dropzone
    this.dropzoneTarget.parentElement.insertBefore(alertDiv, this.dropzoneTarget);
    
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      alertDiv.remove();
    }, 5000);
  }

  getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }

  previewTemplate() {
    return `
      <div class="card border-success">
        <div class="card-body d-flex align-items-center">
          <i class="fas fa-file-pdf fa-3x text-danger me-3" data-dz-file-icon></i>
          <div class="flex-grow-1">
            <strong data-dz-name></strong>
            <div class="text-muted small" data-dz-size></div>
            <div class="progress mt-2" style="height: 4px;">
              <div class="progress-bar" role="progressbar" data-dz-uploadprogress style="width: 0%"></div>
            </div>
          </div>
          <button type="button" class="btn btn-link text-danger ms-auto" data-dz-remove>
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="card-footer bg-danger text-white d-none" data-dz-error-container>
          <small data-dz-errormessage></small>
        </div>
      </div>
    `;
  }
}

